import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { applyExceptionFingerprint } from '@/services/errorFingerprint';

/**
 * React plugin instance for Application Insights
 * Used for automatic route tracking in React applications
 */
const reactPlugin = new ReactPlugin();

/**
 * Browser history instance for route tracking
 */
const browserHistory = createBrowserHistory();

/**
 * Initialize Application Insights with the provided connection string
 * @param connectionString - Application Insights connection string from Azure portal
 * @returns ApplicationInsights instance or null if connection string is not provided
 */
export function initializeAppInsights(connectionString?: string): ApplicationInsights | null {
  // Don't initialize if no connection string provided
  if (!connectionString) {
    console.warn('[AppInsights] No connection string provided, Application Insights will not be initialized');
    return null;
  }

  try {
    const appInsights = new ApplicationInsights({
      config: {
        connectionString,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: { history: browserHistory },
        },
        // Automatic tracking
        enableAutoRouteTracking: true, // Track route changes automatically
        enableRequestHeaderTracking: true, // Include request headers in telemetry
        enableResponseHeaderTracking: true, // Include response headers in telemetry
        disableFetchTracking: false, // Track fetch API calls
        disableAjaxTracking: false, // Track AJAX calls

        // Session tracking
        enableCorsCorrelation: false, // Disable cross-origin correlation to avoid CORS issues with mock servers
        correlationHeaderExcludedDomains: ['localhost:7072', 'localhost:7071', '*.queue.core.windows.net'], // Exclude local servers and Azure Storage

        // Performance
        maxBatchInterval: 5000, // Send telemetry every 5 seconds
        maxBatchSizeInBytes: 102400, // 100KB max batch size

        // Sampling (100% = no sampling, reduce if too much telemetry)
        samplingPercentage: 100,

        // Error tracking
        disableExceptionTracking: false,
        autoTrackPageVisitTime: true,

        // Development settings
        loggingLevelConsole: 0, // Suppress console logs for telemetry errors
        loggingLevelTelemetry: 0, // Don't send internal diagnostic telemetry

        // Disable error notifications for failed telemetry sends
        disableInstrumentationKeyValidation: true,
      },
    });

    appInsights.loadAppInsights();
    // Give auto-collected (uncaught) exceptions a stable fingerprint; ProblemId is built from
    // minified stack frames and changes on every deploy.
    appInsights.addTelemetryInitializer((item) => {
      applyExceptionFingerprint(item);
    });
    console.log('[AppInsights] Application Insights initialized successfully');

    return appInsights;
  } catch (error) {
    console.error('[AppInsights] Failed to initialize Application Insights:', error);
    return null;
  }
}
